import { useState } from 'react';
import {
  HelpCircle, BookOpen, Download, ChevronDown, ChevronRight,
  LayoutDashboard, FileText, Image, Layout, Package, ShoppingCart,
  Users, Telescope, Landmark, Search, Bot, Truck, Settings,
  UserCog, Shield, AlertTriangle, CheckCircle, Info, Phone, Mail,
  Globe, Key, Zap, Lock, RefreshCw, Star, MessageSquare,
} from 'lucide-react';

const BRAND = {
  navy:  '#0a0c12',
  dblue: '#295CA8',
  sblue: '#3D72B8',
  lblue: '#4ab8ff',
};

/* ── Utile ───────────────────────────────────────────────────────────────── */
function Badge({ children, color = 'blue' }: { children: React.ReactNode; color?: string }) {
  const cls: Record<string, string> = {
    blue:   'bg-blue-500/15 text-blue-400 border-blue-500/25',
    green:  'bg-green-500/15 text-green-400 border-green-500/25',
    yellow: 'bg-yellow-500/15 text-yellow-400 border-yellow-500/25',
    red:    'bg-red-500/15 text-red-400 border-red-500/25',
    purple: 'bg-purple-500/15 text-purple-400 border-purple-500/25',
    gray:   'bg-white/5 text-white/40 border-white/10',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${cls[color] || cls.blue}`}>
      {children}
    </span>
  );
}

function InfoBox({ type, children }: { type: 'tip' | 'warn' | 'info'; children: React.ReactNode }) {
  const cfg = {
    tip:  { icon: CheckCircle, bg: 'rgba(74,222,128,0.07)', border: 'rgba(74,222,128,0.25)', text: '#4ade80', label: 'TIP' },
    warn: { icon: AlertTriangle, bg: 'rgba(250,204,21,0.07)', border: 'rgba(250,204,21,0.25)', text: '#facc15', label: 'ATENȚIE' },
    info: { icon: Info, bg: 'rgba(74,184,255,0.07)', border: 'rgba(74,184,255,0.25)', text: '#4ab8ff', label: 'INFO' },
  }[type];
  const Icon = cfg.icon;
  return (
    <div className="flex gap-3 rounded-xl px-4 py-3 my-3"
      style={{ background: cfg.bg, border: `1px solid ${cfg.border}` }}>
      <Icon size={15} className="flex-shrink-0 mt-0.5" style={{ color: cfg.text }} />
      <div className="text-xs leading-relaxed" style={{ color: cfg.text }}>
        <span className="font-bold mr-1">{cfg.label}:</span>
        <span className="text-white/70">{children}</span>
      </div>
    </div>
  );
}

function SectionCard({
  icon: Icon, title, badge, color = BRAND.dblue, children,
}: {
  icon: React.ElementType; title: string; badge?: string; color?: string; children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl border border-white/10 overflow-hidden"
      style={{ background: 'rgba(7,9,15,0.8)' }}>
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center gap-3 px-5 py-4 hover:bg-white/5 transition-colors text-left">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ background: `${color}30`, border: `1px solid ${color}50` }}>
          <Icon size={16} style={{ color }} />
        </div>
        <span className="font-semibold text-white flex-1">{title}</span>
        {badge && <Badge color="gray">{badge}</Badge>}
        {open
          ? <ChevronDown size={16} className="text-white/30 flex-shrink-0" />
          : <ChevronRight size={16} className="text-white/30 flex-shrink-0" />}
      </button>
      {open && (
        <div className="px-5 pb-5 border-t border-white/5 pt-4 space-y-3 text-sm text-white/70 leading-relaxed">
          {children}
        </div>
      )}
    </div>
  );
}

function RoleTable() {
  const sections = [
    { name: 'Dashboard',       management: true,  marketing: true,  vanzari: true  },
    { name: 'Blog',            management: true,  marketing: true,  vanzari: false },
    { name: 'Librărie Media',  management: true,  marketing: true,  vanzari: true  },
    { name: 'Pagini',          management: true,  marketing: true,  vanzari: false },
    { name: 'Produse',         management: true,  marketing: true,  vanzari: true  },
    { name: 'Comenzi',         management: true,  marketing: false, vanzari: true  },
    { name: 'Clienți',         management: true,  marketing: false, vanzari: true  },
    { name: 'Prospecți',       management: true,  marketing: false, vanzari: true  },
    { name: 'Financiar',       management: true,  marketing: false, vanzari: false },
    { name: 'SEO',             management: true,  marketing: true,  vanzari: false },
    { name: 'Chat Bot AI',     management: true,  marketing: false, vanzari: false },
    { name: 'Curierat',        management: true,  marketing: false, vanzari: false },
    { name: 'Setări Web Site', management: true,  marketing: false, vanzari: false },
    { name: 'Utilizatori',     management: true,  marketing: false, vanzari: false },
    { name: 'Help',            management: true,  marketing: true,  vanzari: true  },
  ];
  const Y = () => <span className="text-green-400 font-bold">✓</span>;
  const N = () => <span className="text-white/20">—</span>;
  return (
    <div className="overflow-x-auto rounded-xl border border-white/10">
      <table className="w-full text-xs">
        <thead>
          <tr style={{ background: BRAND.dblue }}>
            <th className="text-left px-4 py-2.5 text-white font-semibold">Secțiune</th>
            <th className="text-center px-4 py-2.5 text-white font-semibold">Management</th>
            <th className="text-center px-4 py-2.5 text-white font-semibold">Marketing</th>
            <th className="text-center px-4 py-2.5 text-white font-semibold">Vânzări</th>
          </tr>
        </thead>
        <tbody>
          {sections.map((s, i) => (
            <tr key={s.name} style={{ background: i % 2 === 0 ? 'rgba(41,92,168,0.05)' : 'transparent' }}>
              <td className="px-4 py-2 text-white/70 font-medium">{s.name}</td>
              <td className="px-4 py-2 text-center">{s.management ? <Y /> : <N />}</td>
              <td className="px-4 py-2 text-center">{s.marketing ? <Y /> : <N />}</td>
              <td className="px-4 py-2 text-center">{s.vanzari ? <Y /> : <N />}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TroubleshootTable() {
  const rows = [
    ['Dashboard fără date GA4', 'OAuth GA4 neautorizat', 'Setări → Google API → Conectează GA4'],
    ['Chatbot "Deconectat"', 'Cheie OpenAI invalidă / fără credit', 'Chat Bot AI → Configurare → Testează'],
    ['Librărie Media goală', 'PHP indisponibil pe CDN preview', 'Normal pe preview; pe server live funcționează'],
    ['Nu pot edita o pagină', 'Rol fără permisiune', 'Contactați Management pentru acces'],
    ['AWB nu se generează', 'Credențiale curier lipsă', 'Setări → Curierat → configurați API-ul'],
    ['Emailuri netrimise', 'SMTP neconfigurat', 'Setări → verificați datele SMTP'],
    ['Analiza prospect eșuează', 'PHP / PageSpeed API lipsă', 'Pe server live: adăugați PAGESPEED_API_KEY în config.php'],
    ['Imagini lipsă în articol', 'URL extern în loc de librărie internă', 'Blog → editați → înlocuiți cu imagini din librărie'],
  ];
  return (
    <div className="overflow-x-auto rounded-xl border border-white/10">
      <table className="w-full text-xs">
        <thead>
          <tr style={{ background: 'rgba(41,92,168,0.6)' }}>
            <th className="text-left px-4 py-2.5 text-white font-semibold">Problemă</th>
            <th className="text-left px-4 py-2.5 text-white font-semibold">Cauză</th>
            <th className="text-left px-4 py-2.5 text-white font-semibold">Soluție</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(([p, c, s], i) => (
            <tr key={i} style={{ background: i % 2 === 0 ? 'rgba(41,92,168,0.05)' : 'transparent' }}>
              <td className="px-4 py-2.5 text-red-300 font-medium align-top">{p}</td>
              <td className="px-4 py-2.5 text-yellow-300/80 align-top">{c}</td>
              <td className="px-4 py-2.5 text-green-300 align-top">{s}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ── Componenta principala ───────────────────────────────────────────────── */
export default function HelpPage() {
  const [activeTab, setActiveTab] = useState<'guide' | 'faq' | 'roles' | 'support'>('guide');

  const handleDownloadPDF = () => {
    const link = document.createElement('a');
    link.href = '/MANUAL_CMS_INTERPERSONAL.pdf';
    link.download = 'Manual_CMS_INTERPERSONAL_v2026.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="space-y-6 max-w-5xl">

      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: 'linear-gradient(135deg,#295CA8,#4ab8ff)', boxShadow: '0 0 24px rgba(74,184,255,0.3)' }}>
            <HelpCircle size={24} className="text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Help & Documentație</h1>
            <p className="text-sm text-white/40 mt-0.5">Manual complet de utilizare a platformei CMS INTERPERSONAL</p>
          </div>
        </div>

        {/* Download PDF */}
        <button onClick={handleDownloadPDF}
          className="flex items-center gap-2.5 px-5 py-3 rounded-xl font-semibold text-sm transition-all hover:scale-105 active:scale-95 flex-shrink-0"
          style={{ background: 'linear-gradient(135deg,#295CA8,#4ab8ff)', color: '#fff', boxShadow: '0 4px 20px rgba(74,184,255,0.25)' }}>
          <Download size={17} />
          Descarcă Manual PDF
        </button>
      </div>

      {/* PDF preview card */}
      <div className="flex items-center gap-4 rounded-xl p-4"
        style={{ background: 'rgba(41,92,168,0.1)', border: '1px solid rgba(74,184,255,0.2)' }}>
        <div className="w-10 h-12 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ background: 'rgba(41,92,168,0.3)', border: '1px solid rgba(74,184,255,0.3)' }}>
          <BookOpen size={20} className="text-[#4ab8ff]" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-white">Manual CMS INTERPERSONAL S.R.L.</p>
          <p className="text-xs text-white/40 mt-0.5">Versiunea v2026.05 · 19 capitole · PDF format A4</p>
          <div className="flex flex-wrap gap-2 mt-2">
            {['Dashboard','Blog','Media','Pagini','Produse','Comenzi','Clienți','Prospecți','SEO','ChatBot','Curierat','RBAC','Securitate'].map(t => (
              <span key={t} className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 text-white/40">{t}</span>
            ))}
          </div>
        </div>
        <button onClick={handleDownloadPDF}
          className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium flex-shrink-0 transition-all hover:bg-[#295CA8]/50"
          style={{ background: 'rgba(41,92,168,0.2)', border: '1px solid rgba(74,184,255,0.2)', color: '#4ab8ff' }}>
          <Download size={13} /> PDF
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-white/10">
        {([
          ['guide',   BookOpen,     'Ghid Rapid'],
          ['faq',     MessageSquare,'Întrebări Frecvente'],
          ['roles',   Shield,       'Roluri & Permisiuni'],
          ['support', Phone,        'Contact Suport'],
        ] as const).map(([t, Icon, label]) => (
          <button key={t} onClick={() => setActiveTab(t)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors border-b-2 ${
              activeTab === t
                ? 'text-[#4ab8ff] border-[#295CA8]'
                : 'text-white/40 border-transparent hover:text-white hover:border-white/20'
            }`}>
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>

      {/* ── TAB: GHID RAPID ── */}
      {activeTab === 'guide' && (
        <div className="space-y-3">
          <p className="text-sm text-white/50 mb-4">
            Faceți click pe orice secțiune pentru a o extinde și a citi ghidul rapid.
            Pentru documentație completă descărcați manualul PDF de mai sus.
          </p>

          <SectionCard icon={LayoutDashboard} title="Dashboard" badge="Toți utilizatorii" color={BRAND.dblue}>
            <p>Pagina principală cu indicatori de performanță (KPI-uri), trafic Google Analytics și statistici comenzi.</p>
            <ul className="space-y-1.5 mt-2">
              <li>• <strong className="text-white">Trafic GA4</strong> — sesiuni, utilizatori, pagini vizualizate din Google Analytics</li>
              <li>• <strong className="text-white">Indicator LIVE/OffLine</strong> — colțul dreapta-jos; verde = date reale GA4 active</li>
              <li>• <strong className="text-white">Comenzi noi</strong> — numărul comenzilor cu status "În așteptare"</li>
              <li>• <strong className="text-white">Venit lunar</strong> — suma totală comenzi luna curentă</li>
            </ul>
            <InfoBox type="tip">Pentru date GA4 reale: Setări Web Site → Google API → introduceți credențialele și conectați.</InfoBox>
          </SectionCard>

          <SectionCard icon={FileText} title="Blog — Gestionare Articole" badge="Management · Marketing" color={'#f59e0b'}>
            <p>Creați, editați și publicați articole pe blog-ul site-ului public (/blog).</p>
            <ul className="space-y-1.5 mt-2">
              <li>• <strong className="text-white">Editor Rich Text</strong> — bold, italic, titluri H1-H3, liste, linkuri, imagini</li>
              <li>• <strong className="text-white">Imagini</strong> — NUMAI din Librăria Media internă (nu URL-uri externe)</li>
              <li>• <strong className="text-white">Categorii</strong> — Web Design, Call Center, IT Hardware, Găzduire, VoIP, CRM</li>
              <li>• <strong className="text-white">SEO</strong> — titlu (max 60 car.), descriere (max 160 car.), slug URL personalizat</li>
              <li>• <strong className="text-white">Status</strong> — Draft (invizibil pe site) sau Publicat</li>
            </ul>
            <InfoBox type="warn">Toate imaginile din articole trebuie să fie din Librăria Media. URL-urile externe Unsplash nu sunt permise.</InfoBox>
          </SectionCard>

          <SectionCard icon={Image} title="Librărie Media" badge="Toți utilizatorii" color={'#8b5cf6'}>
            <p>Centrala fișierelor multimedia: imagini, video, audio folosite pe întreg site-ul.</p>
            <ul className="space-y-1.5 mt-2">
              <li>• <strong className="text-white">Upload</strong> — drag & drop sau click; formate: JPG, PNG, WebP, MP4, MP3</li>
              <li>• <strong className="text-white">Conversie auto</strong> — JPG/JPEG se convertesc automat în WebP (PNG rămâne neschimbat)</li>
              <li>• <strong className="text-white">URL intern</strong> — fiecare fișier primește un URL vizibil în card</li>
              <li>• <strong className="text-white">Badge-uri utilizare</strong> — arată unde este folosit fiecare fișier (Blog, Produs, Pagină)</li>
            </ul>
            <InfoBox type="info">PNG-urile NU se convertesc la WebP — sunt păstrate în formatul original.</InfoBox>
          </SectionCard>

          <SectionCard icon={Layout} title="Pagini — Editor Texte Site" badge="Management · Marketing" color={BRAND.sblue}>
            <p>Editați textele afișate pe paginile publice fără a modifica codul sursă.</p>
            <ul className="space-y-1.5 mt-2">
              <li>• <strong className="text-white">12 pagini editabile</strong>: Home, Servicii (6), Contact, Termeni, GDPR, Cookies, Retur, NDA</li>
              <li>• <strong className="text-white">Secțiuni independente</strong> per pagină (Hero, Intro, Caracteristici, etc.)</li>
              <li>• <strong className="text-white">Previzualizare</strong> — buton pentru verificare live după editare</li>
              <li>• <strong className="text-white">Salvare imediată</strong> — modificările devin vizibile pe site instant</li>
            </ul>
          </SectionCard>

          <SectionCard icon={Package} title="Produse — Catalog și Editor" badge="Management · Marketing · Vânzări" color={'#10b981'}>
            <p>Gestionați catalogul de 63+ produse din 4 categorii: IT Hardware, Hosting, Servicii.</p>
            <ul className="space-y-1.5 mt-2">
              <li>• <strong className="text-white">Tab Principal</strong> — denumire, categorie, prețuri (vânzare + furnizor), descriere rich text</li>
              <li>• <strong className="text-white">Tab Galerie</strong> — imagine copertă + galerie imagini adiționale din Librăria Media</li>
              <li>• <strong className="text-white">Tab SEO</strong> — meta title/description cu counter + preview Google + slug URL</li>
              <li>• <strong className="text-white">Tab Relații</strong> — produse similare afișate pe pagina produsului</li>
              <li>• <strong className="text-white">Import</strong> — din feed URL / CSV / XML (sub-tab Import)</li>
            </ul>
            <InfoBox type="info">Prețul furnizorului și marja sunt vizibile NUMAI în admin, niciodată pe site-ul public.</InfoBox>
          </SectionCard>

          <SectionCard icon={ShoppingCart} title="Comenzi — Gestionare și AWB" badge="Management · Vânzări" color={'#f97316'}>
            <p>Gestionați toate comenzile plasate pe site sau create manual din admin.</p>
            <ul className="space-y-1.5 mt-2">
              <li>• <strong className="text-white">Statusuri</strong>: În așteptare → Confirmat → În execuție → Finalizat / Anulat</li>
              <li>• <strong className="text-white">Note client</strong> — vizibile cu fundal portocaliu când clientul a adăugat note la plasare</li>
              <li>• <strong className="text-white">Adrese</strong> — facturare (5 câmpuri) + livrare cu toggle "Identică cu facturarea"</li>
              <li>• <strong className="text-white">AWB</strong> — buton "Generează AWB" → modal cu selectare curier și date transport</li>
              <li>• <strong className="text-white">Coșuri abandonate</strong> — tab separat cu sesiuni necompletate</li>
            </ul>
            <InfoBox type="warn">AWB-ul funcționează complet doar pe serverul live cu credențialele API curier configurate în Setări → Curierat.</InfoBox>
          </SectionCard>

          <SectionCard icon={Users} title="Clienți — Baza de Date" badge="Management · Vânzări" color={'#06b6d4'}>
            <p>Baza de date a clienților activi cu istoric comenzi și date de contact complete.</p>
            <ul className="space-y-1.5 mt-2">
              <li>• <strong className="text-white">Câmpuri</strong>: nume/firmă, CUI, email, telefon, adresă facturare, persoană contact, note</li>
              <li>• <strong className="text-white">Istoric comenzi</strong> — vizualizare toate comenzile per client</li>
              <li>• <strong className="text-white">Adăugare manuală</strong> — fără a fi necesară o comandă</li>
              <li>• <strong className="text-white">Status</strong>: Activ / Inactiv / Prospect</li>
            </ul>
          </SectionCard>

          <SectionCard icon={Telescope} title="Prospecți — Analiză Automată" badge="Management · Vânzări" color={'#a855f7'}>
            <p>Instrument de vânzare: analizați automat site-ul unui potențial client pentru argumentarea ofertei.</p>
            <ul className="space-y-1.5 mt-2">
              <li>• <strong className="text-white">PageSpeed</strong> — scor mobil/desktop, FCP, LCP, CLS, TBT</li>
              <li>• <strong className="text-white">Tehnologie</strong> — CMS detectat, stack tehnic, versiuni</li>
              <li>• <strong className="text-white">WHOIS .ro</strong> — proprietar domeniu, dată expirare, nameservere</li>
              <li>• <strong className="text-white">Date ANAF</strong> — cifră afaceri, profit, angajați, CAEN</li>
              <li>• <strong className="text-white">Probleme SEO</strong> — lista completă cu erori și recomandări</li>
            </ul>
            <InfoBox type="info">Analiza completă funcționează pe serverul live cu PHP activ și cheie API Google PageSpeed configurată în config.php.</InfoBox>
          </SectionCard>

          <SectionCard icon={Bot} title="Chat Bot AI" badge="Management" color={BRAND.lblue}>
            <p>Configurare și monitorizare asistent virtual afișat pe site-ul public.</p>
            <ul className="space-y-1.5 mt-2">
              <li>• <strong className="text-white">Tab Configurare</strong>: cheie API OpenAI, model (gpt-5.4-mini recomandat), test conexiune</li>
              <li>• <strong className="text-white">Status</strong>: 🟢 Activ (cheie validă) / 🔴 Deconectat (eroare)</li>
              <li>• <strong className="text-white">Tab Interacțiuni</strong>: log-uri conversații clienți cu chatbot-ul</li>
              <li>• <strong className="text-white">Model recomandat</strong>: gpt-5.4-mini — rapid și economic</li>
            </ul>
            <InfoBox type="tip">Obțineți cheia API de pe platform.openai.com/api-keys. Asigurați-vă că aveți credit (minim $5 USD).</InfoBox>
          </SectionCard>

          <SectionCard icon={Truck} title="Curierat — Furnizori AWB" badge="Management" color={'#64748b'}>
            <p>Configurare credențiale API pentru fiecare furnizor de curierat integrat.</p>
            <ul className="space-y-1.5 mt-2">
              <li>• <strong className="text-white">Furnizori</strong>: Fan Courier, DPD, Cargus, GLS, SameDay</li>
              <li>• <strong className="text-white">Câmpuri</strong>: Username, Parolă/API Key, Client ID (diferit per furnizor)</li>
              <li>• <strong className="text-white">Obținere credențiale</strong>: din portalul business al fiecărui furnizor</li>
            </ul>
          </SectionCard>

          <SectionCard icon={Settings} title="Setări Web Site" badge="Management" color={'#6366f1'}>
            <p>Configurări tehnice globale ale platformei: date companie, integrări Google, SMTP, reCAPTCHA.</p>
            <ul className="space-y-1.5 mt-2">
              <li>• <strong className="text-white">Google API</strong>: Client ID OAuth2, Secret, GA4 Property ID (461287871)</li>
              <li>• <strong className="text-white">Analytics</strong>: GA4 ID (G-6CJP50W0MD), GTM ID (GTM-5ZW5DDR)</li>
              <li>• <strong className="text-white">reCAPTCHA</strong>: Site Key și Secret Key pentru formularul de contact</li>
              <li>• <strong className="text-white">SMTP</strong>: server, port, user, parolă pentru notificări email</li>
            </ul>
          </SectionCard>

          <SectionCard icon={UserCog} title="Utilizatori & Permisiuni (RBAC)" badge="Management" color={'#ec4899'}>
            <p>Gestionare completă a utilizatorilor administrativi cu control granular al drepturilor de acces.</p>
            <ul className="space-y-1.5 mt-2">
              <li>• <strong className="text-white">3 roluri</strong>: Management (complet), Marketing, Vânzări</li>
              <li>• <strong className="text-white">CRUD useri</strong>: creare, editare, dezactivare, ștergere</li>
              <li>• <strong className="text-white">Schimbare parolă</strong>: din propriul profil sau Management pentru oricine</li>
              <li>• <strong className="text-white">Jurnal activitate</strong>: toate acțiunile per utilizator cu timestamp</li>
            </ul>
            <InfoBox type="warn">Schimbați parolele implicite imediat după prima conectare!</InfoBox>
          </SectionCard>
        </div>
      )}

      {/* ── TAB: FAQ ── */}
      {activeTab === 'faq' && (
        <div className="space-y-4">
          <div className="mb-6">
            <h2 className="text-lg font-bold text-white mb-1">Întrebări Frecvente</h2>
            <p className="text-sm text-white/40">Probleme comune și soluțiile lor rapide.</p>
          </div>

          <TroubleshootTable />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
            {[
              {
                q: 'De ce nu văd date în Dashboard?',
                a: 'Datele GA4 necesită autentificare OAuth2. Mergeți la Setări Web Site → Google API → apăsați "Conectează Google Analytics" și autorizați accesul cu contul Google care are acces la proprietatea GA4.',
                icon: LayoutDashboard, color: BRAND.dblue,
              },
              {
                q: 'Cum schimb parola?',
                a: 'Click pe email-ul din header (colțul dreapta-sus) → dropdown → "Schimbă Parola". Sau dacă aveți rol Management: Utilizatori → selectați userul → "Schimbă Parola".',
                icon: Key, color: '#8b5cf6',
              },
              {
                q: 'Pot adăuga un utilizator nou?',
                a: 'Numai utilizatorii cu rol Management pot adăuga useri noi. Mergeți la Utilizatori → tab "Management Utilizatori" → buton "Utilizator Nou".',
                icon: UserCog, color: '#ec4899',
              },
              {
                q: 'De ce chatbot-ul arată Deconectat?',
                a: 'Cheia API OpenAI este invalidă sau nu mai are credit. Mergeți la Chat Bot AI → Configurare → introduceți o cheie nouă → apăsați "Salvează" → "Test".',
                icon: Bot, color: BRAND.lblue,
              },
              {
                q: 'Cum adaug un produs nou?',
                a: 'Produse → buton "Produs Nou" (sau "+") → completați tab-ul Principal, adăugați imagini în tab Galerie, setați meta SEO în tab SEO → Salvează.',
                icon: Package, color: '#10b981',
              },
              {
                q: 'Cum generez un AWB?',
                a: 'Comenzi → click pe comandă → buton "Generează AWB" → selectați curatorul → completați datele → Generează. Necesită credențiale curier configurate în Setări → Curierat.',
                icon: Truck, color: '#f97316',
              },
              {
                q: 'Pot importa produse dintr-un fișier?',
                a: 'Da. Produse → sub-tab "Import" → alegeți sursa (Feed URL / CSV / XML) → încărcați fișierul → Importează. Structura CSV: id, name, price, category, description.',
                icon: RefreshCw, color: '#06b6d4',
              },
              {
                q: 'Cum adaug un articol pe blog cu imagine?',
                a: 'Blog → "Articol Nou" → completați titlul și conținutul → în editor apăsați butonul "Imagine" → alegeți din Librăria Media (sau încărcați mai întâi în Librărie Media).',
                icon: FileText, color: '#f59e0b',
              },
            ].map(({ q, a, icon: Icon, color }) => (
              <div key={q} className="rounded-xl p-4 space-y-3"
                style={{ background: 'rgba(7,9,15,0.8)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <div className="flex gap-3 items-start">
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
                    style={{ background: `${color}25`, border: `1px solid ${color}40` }}>
                    <Icon size={14} style={{ color }} />
                  </div>
                  <p className="text-sm font-semibold text-white leading-snug">{q}</p>
                </div>
                <p className="text-xs text-white/60 leading-relaxed pl-10">{a}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── TAB: ROLURI ── */}
      {activeTab === 'roles' && (
        <div className="space-y-6">
          <div>
            <h2 className="text-lg font-bold text-white mb-1">Roluri și Permisiuni</h2>
            <p className="text-sm text-white/40">Matricea completă de acces per secțiune și rol.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              {
                role: 'Management',
                color: '#a855f7',
                badge: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
                desc: 'Acces complet la toate secțiunile. Poate gestiona utilizatori, setări, configurații avansate.',
                sections: ['Dashboard','Blog','Media','Pagini','Produse','Comenzi','Clienți','Prospecți','Financiar','SEO','ChatBot','Curierat','Setări','Utilizatori','Help'],
              },
              {
                role: 'Marketing',
                color: '#facc15',
                badge: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
                desc: 'Acces la conținut și SEO. Nu poate vedea comenzi, clienți sau configurații tehnice.',
                sections: ['Dashboard','Blog','Media','Pagini','Produse (+ SEO + Galerie)','SEO','Help'],
              },
              {
                role: 'Vânzări',
                color: '#4ade80',
                badge: 'bg-green-500/20 text-green-400 border-green-500/30',
                desc: 'Acces la fluxul de vânzări. Nu poate edita conținut sau configurații tehnice.',
                sections: ['Dashboard','Produse','Comenzi','Clienți','Prospecți','Media','Help'],
              },
            ].map(({ role, color, badge, desc, sections }) => (
              <div key={role} className="rounded-xl p-5 space-y-4"
                style={{ background: 'rgba(7,9,15,0.8)', border: `1px solid ${color}25` }}>
                <div className="flex items-center gap-2">
                  <Shield size={16} style={{ color }} />
                  <span className={`px-2.5 py-1 rounded-full text-xs font-bold border ${badge}`}>{role}</span>
                </div>
                <p className="text-xs text-white/50 leading-relaxed">{desc}</p>
                <div className="space-y-1">
                  <p className="text-[10px] uppercase tracking-wide text-white/30 font-semibold mb-2">Secțiuni vizibile</p>
                  {sections.map(s => (
                    <div key={s} className="flex items-center gap-2 text-xs text-white/70">
                      <CheckCircle size={11} style={{ color }} />
                      {s}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div>
            <h3 className="text-sm font-bold text-white mb-3">Matrice completă acces</h3>
            <RoleTable />
          </div>

          <InfoBox type="tip">
            Utilizatorul <strong>admin</strong> are rol Management și nu poate fi șters. Creați useri suplimentari din
            Utilizatori → Management Utilizatori → Utilizator Nou.
          </InfoBox>
        </div>
      )}

      {/* ── TAB: SUPORT ── */}
      {activeTab === 'support' && (
        <div className="space-y-6">
          <div>
            <h2 className="text-lg font-bold text-white mb-1">Contact Suport Tehnic</h2>
            <p className="text-sm text-white/40">Echipa INTERPERSONAL S.R.L. vă stă la dispoziție.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              {
                icon: Mail, title: 'Email Suport',
                value: 'office@interpersonal.ro',
                desc: 'Răspuns în max 4 ore în timpul programului',
                color: BRAND.dblue, action: 'mailto:office@interpersonal.ro',
              },
              {
                icon: Phone, title: 'Telefon Principal',
                value: '+40 741 407 595',
                desc: 'Luni–Vineri 09:00–18:00',
                color: '#10b981', action: 'tel:+40741407595',
              },
              {
                icon: Phone, title: 'Telefon Tehnic',
                value: '+40 758 011 010',
                desc: 'Suport tehnic disponibil 24/7',
                color: '#f59e0b', action: 'tel:+40758011010',
              },
              {
                icon: Globe, title: 'Website',
                value: 'interpersonal.ro',
                desc: 'Documentație și noutăți',
                color: '#8b5cf6', action: 'https://interpersonal.ro',
              },
            ].map(({ icon: Icon, title, value, desc, color, action }) => (
              <a key={title} href={action} target={action.startsWith('http') ? '_blank' : undefined}
                rel="noreferrer"
                className="flex items-center gap-4 rounded-xl p-5 transition-all hover:scale-[1.02]"
                style={{ background: 'rgba(7,9,15,0.8)', border: `1px solid ${color}25` }}>
                <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: `${color}20`, border: `1px solid ${color}40` }}>
                  <Icon size={20} style={{ color }} />
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-white/40 mb-0.5">{title}</p>
                  <p className="text-sm font-bold text-white">{value}</p>
                  <p className="text-xs text-white/30 mt-0.5">{desc}</p>
                </div>
              </a>
            ))}
          </div>

          {/* Date companie */}
          <div className="rounded-xl p-5 space-y-3"
            style={{ background: 'rgba(41,92,168,0.08)', border: '1px solid rgba(74,184,255,0.15)' }}>
            <p className="text-sm font-bold text-white flex items-center gap-2">
              <Zap size={15} className="text-[#4ab8ff]" /> INTERPERSONAL S.R.L.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs text-white/50">
              <div><span className="text-white/30">CUI:</span> RO34799221</div>
              <div><span className="text-white/30">Reg. Com.:</span> J2015002538235</div>
              <div className="md:col-span-2"><span className="text-white/30">Adresă:</span> Str. Orhideelor nr. 8, Sat Țegheș, Com. Domnești, Jud. Ilfov, 077091</div>
              <div><span className="text-white/30">Certificări:</span> ISO 9001 · ISO 27001</div>
              <div><span className="text-white/30">Program suport:</span> L-V 09:00–18:00 · Tehnic 24/7</div>
            </div>
          </div>

          {/* Version info */}
          <div className="rounded-xl p-4 flex items-center gap-3"
            style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
            <Star size={14} className="text-[#4ab8ff]" />
            <div className="text-xs text-white/40">
              <span className="text-white/60 font-semibold">CMS INTERPERSONAL</span> · Versiune v2026.05 ·
              Manual PDF disponibil pentru descărcare · Documentație actualizată Mai 2026
            </div>
            <button onClick={handleDownloadPDF}
              className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium flex-shrink-0 transition-all"
              style={{ background: 'rgba(41,92,168,0.3)', border: '1px solid rgba(74,184,255,0.2)', color: '#4ab8ff' }}>
              <Download size={12} /> Manual PDF
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
